import * as React from 'react'

import {
  useGetIngestConversationQuery,
  type AnsweredIngestQuestion,
  type IngestOperatorMessage,
  type IngestSection
} from '@axilon-apps/domains-ontology-ui-state'

import { cn } from '../../lib/utils'
import { PastConversationTurns } from '../Chat/PastConversationTurns'
import { Conversation } from './Conversation'
import {
  DEFAULT_INGEST_CHAT_MODE,
  IngestChatModeTabs,
  type IngestChatMode
} from './IngestChatModeTabs'
import { readIngestQueryConversationId } from './ingestQueryConversationStorage'

interface IngestConversationHistoryProps {
  jobId: string
  conversationId: string
  branchId?: string
  answered: readonly AnsweredIngestQuestion[]
  messages: readonly IngestOperatorMessage[]
  sections: readonly Pick<IngestSection, 'id' | 'name' | 'status'>[]
  jobEndStatus: 'done' | 'failed' | 'aborted'
  fileName?: string
  startedAt?: string
  additionalContext?: string
}

/**
 * Read-only Ask / Agent history for an ingest whose branch is no longer
 * active. Loads each conversation once — no live-session polling, SSE,
 * composers, or approval controls. Both panes stay mounted so tab switches
 * do not remount / refetch.
 */
export function IngestConversationHistory({
  jobId,
  conversationId,
  branchId,
  answered,
  messages,
  sections,
  jobEndStatus,
  fileName,
  startedAt,
  additionalContext
}: IngestConversationHistoryProps) {
  const [chatTab, setChatTab] = React.useState<IngestChatMode>(DEFAULT_INGEST_CHAT_MODE)
  const { data: conversation } = useGetIngestConversationQuery(conversationId, {
    skip: !conversationId,
    refetchOnMountOrArgChange: true
  })

  return (
    <section className="flex min-w-0 flex-col gap-2.5">
      <h2 className="m-0 px-0.5 text-11 font-medium tracking-[0.08em] text-muted-foreground uppercase">
        Conversation history
      </h2>

      <IngestChatModeTabs
        value={chatTab}
        onChange={setChatTab}
        ariaLabel="Ingest conversation history"
        className="px-0.5"
      />

      <div className={cn(chatTab !== 'ask' && 'hidden')}>
        <IngestAskHistory jobId={jobId} />
      </div>
      <div className={cn(chatTab !== 'agent' && 'hidden')}>
        <Conversation
          jobId={jobId}
          branchId={branchId}
          pending={[]}
          answered={answered}
          messages={messages}
          transcript={conversation?.runs}
          sections={sections}
          fileName={fileName}
          startedAt={startedAt}
          additionalContext={additionalContext}
          active={false}
          readOnly
          jobEndStatus={jobEndStatus}
          layout="page"
        />
      </div>
    </section>
  )
}

/**
 * One-shot Ask-lane transcript. Uses the job-scoped conversation id from
 * session storage when present; otherwise shows an empty history state.
 */
function IngestAskHistory({ jobId }: { jobId: string }) {
  const askConversationId = React.useMemo(() => readIngestQueryConversationId(jobId), [jobId])

  if (!askConversationId) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-card p-4 text-center text-xs text-muted-foreground">
        No Ask conversation was recorded for this job.
      </div>
    )
  }

  return (
    <PastConversationTurns
      conversationId={askConversationId}
      emptyText="This Ask conversation has no turns."
      loadingText="Loading Ask conversation…"
      loadErrorText="Failed to load Ask conversation."
    />
  )
}
